import { useState } from "react";
import { siteConfig, aboutData } from "@/mocks/siteData";
import SEOMeta from "@/components/feature/SEOMeta";
import {
  OrganizationSchema,
  WebPageSchema,
  FAQSchema,
} from "@/components/feature/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

function ContactForm() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("phone_alt") as string || "").trim();

    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    setFormStatus("idle");
    setFormError("");

    try {
      const urlParams = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "phone_alt") {
          urlParams.append(key, value as string);
        }
      });

      const response = await fetch("https://readdy.ai/api/form/d9n1gau8mbnljin59sqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlParams.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg =
          (parsed?.meta as Record<string, unknown>)?.message as string ||
          (parsed?.meta as Record<string, unknown>)?.detail as string ||
          (parsed?.message as string) ||
          responseText ||
          "提交失败，请稍后重试";
        if (typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam")) {
          setFormError("提交失败，请稍后重试");
          setFormStatus("error");
        } else {
          setFormError(serverMsg);
          setFormStatus("error");
        }
      }
    } catch {
      setFormError("网络错误，请稍后重试");
      setFormStatus("error");
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="contact-form-about">
      <div className="space-y-3">
        <div>
          <input
            type="text"
            name="name"
            placeholder="您的姓名"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
          />
        </div>
        <div>
          <input
            type="email"
            name="email"
            placeholder="您的邮箱"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
          />
        </div>
        <div>
          <textarea
            name="message"
            placeholder="请输入您的消息（500字以内）"
            rows={3}
            maxLength={500}
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
          ></textarea>
        </div>
        <div className="anti-spam-field">
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>
        <button
          type="submit"
          className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          发送消息
        </button>
        {formStatus === "success" && (
          <p className="text-sm text-green-600 text-center">消息已发送，我们会尽快回复您！</p>
        )}
        {formStatus === "error" && (
          <p className="text-sm text-red-500 text-center">{formError || "提交失败，请稍后重试"}</p>
        )}
      </div>
    </form>
  );
}

export default function AboutPage() {
  const pageTitle = `关于我们 - ${siteConfig.name}`;
  const pageUrl = `${siteConfig.url}/about`;

  return (
    <>
      <SEOMeta
        title={pageTitle}
        description={`${siteConfig.name}品牌介绍、口碑评价与联系方式。${siteConfig.description}`}
        keywords="91视频关于我们,91视频品牌介绍,91视频联系方式,91视频口碑评价"
        canonicalUrl={pageUrl}
        ogImage={siteConfig.ogImage}
      />
      <OrganizationSchema />
      <WebPageSchema
        title={pageTitle}
        description={`${siteConfig.name}品牌介绍、口碑评价与联系方式`}
        url={pageUrl}
        breadcrumbItems={[
          { name: "首页", item: siteConfig.url },
          { name: "关于我们", item: pageUrl },
        ]}
      />
      <FAQSchema questions={aboutData.faq} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6 max-w-[1000px] mx-auto">
            <Breadcrumb
              items={[{ label: "首页", href: "/" }, { label: "关于我们" }]}
            />

            {/* Brand Introduction */}
            <section className="mt-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
                关于{aboutData.brandName}
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
                <div>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {aboutData.brandIntro}
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {aboutData.mission}
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3] bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20environment%20with%20film%20posters%20on%20walls%2C%20creative%20media%20company%20workspace%2C%20warm%20lighting%2C%20wooden%20furniture%2C%20large%20screens%20displaying%20video%20content%2C%20professional%20atmosphere%2C%20contemporary%20Chinese%20design%20aesthetic&width=800&height=600&seq=about-brand-91video&orientation=landscape"
                    alt={`${aboutData.brandName} - 品牌形象`}
                    title={`${aboutData.brandName} - 国产影视平台`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Achievements */}
            <section className="mt-12 md:mt-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6 text-center">
                我们的成绩
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {aboutData.achievements.map((item, idx) => (
                  <div
                    key={idx}
                    className="text-center p-5 md:p-6 rounded-xl bg-background-100"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-accent-500 font-heading">
                      {item.number}
                    </div>
                    <div className="text-sm text-foreground-600 mt-2">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Vision & Team */}
            <section className="mt-12 md:mt-16">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="p-6 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4">
                    <i className="ri-eye-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-3">
                    我们的愿景
                  </h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {aboutData.vision}
                  </p>
                </div>
                <div className="p-6 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 mb-4">
                    <i className="ri-team-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-3">
                    团队介绍
                  </h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {aboutData.teamIntro}
                  </p>
                </div>
              </div>
            </section>

            {/* User Reviews */}
            <section className="mt-12 md:mt-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {aboutData.reviews.map((review, idx) => (
                  <div key={idx} className="p-5 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className="ri-star-fill text-accent-500 text-sm"
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      &ldquo;{review.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                        {review.name[0]}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">
                          {review.name}
                        </div>
                        <div className="text-xs text-foreground-500">{review.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mt-12 md:mt-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-4">
                    联系方式
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <span className="text-sm text-foreground-700">
                        {aboutData.contact.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <span className="text-sm text-foreground-700">
                        {aboutData.contact.phone}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <span className="text-sm text-foreground-700">
                        {aboutData.contact.address}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <span className="text-sm text-foreground-700">
                        {aboutData.contact.wechat}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-4">
                    发送消息
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="mt-12 md:mt-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6 text-center">
                常见问题
              </h2>
              <div className="space-y-3 max-w-2xl mx-auto">
                {aboutData.faq.map((item, idx) => (
                  <details
                    key={idx}
                    className="group rounded-xl bg-background-100 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                      <span className="text-sm font-medium text-foreground-900 pr-4">
                        {item.question}
                      </span>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-700 leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}